<template>
  <!-- eslint-disable prefer-standard-components/no-vuetify-inputs -->
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
    :sm="sm"
  >
    <v-row dense>
      <v-col
        v-if="label"
        :class="labelClass"
        cols="12"
      >
        {{ label }}

        <RequiredIndicator v-if="mandatory" />

        <HelpIcon
          :document="helpDocument"
          :on-help-click="openHelpFlyout"
        />
      </v-col>

      <v-col
        v-if="description"
        class="fs-16 c-light-black my-1"
        cols="12"
      >
        <span>
          {{ $bct(description) }}
        </span>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-text-field
          v-model.number="localValue"
          @click:append="increment()"
          @click:prepend="decrement()"
          @keydown="preventNonNumericInput"
          @paste="handlePaste"
          @update:model-value="handleUpdateModelValue"
          :append-inner-icon="appendIcon"
          :aria-label="ariaLabel"
          :autofocus="autofocus"
          :class="inputClass"
          :density="dense ? 'compact' : undefined"
          :disabled="disabled || locked"
          :hide-details="hideDetails"
          :label="label"
          :max="max"
          :message="$bct(message || '')"
          :min="min"
          :readonly="readonly"
          :rules="rules"
          :suffix="$bct(suffix)"
          append-icon="add"
          data-cy="labeled-number-field"
          data-testid="labeled-number-field"
          prepend-icon="remove"
          type="number"
          variant="filled"
          flat
          hide-spin-buttons
          round
          tile
        />
      </v-col>
    </v-row>

    <v-divider
      v-if="dividedBottom"
      class="mt-4 mb-2"
    />
  </v-col>
</template>

<script setup>
import { formatInteger, formatNonNegativeInteger } from '@/shared/utils/number-utils';
import HelpIcon from '@/shared/components/form/HelpIcon.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const $bct = inject('translator');

const attrs = useAttrs();
const emit = defineEmits(useLabeledField.emits);
defineExpose({ attrs, emit }); // Attrs and emit are used in composable, this is included for linter

const localValue = defineModel({ type: undefined });

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  dividedBottom: {
    type: Boolean,
    default: false,
  },
  hideDetails: {
    type: Boolean,
    default: false,
  },
  max: {
    type: Number,
    default: null,
  },
  min: {
    type: Number,
    default: 0,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  suffix: {
    type: String,
    default: null,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const { appendIcon, inputClass, labelClass, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { ariaLabel, enabled, helpDocument, label, locked, openHelpFlyout } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

const rules = computed(() => {
  if (!props.enforceMinMaxRules) return [];
  return [
    (value) =>
      props.min === null ||
      value >= props.min ||
      `${$bct('Value must be greater than')} ${props.min}`,
    (value) =>
      props.max === null || value <= props.max || `${$bct('Value must be less than')} ${props.min}`,
  ];
});

const allowNegative = computed(() => props.min === null || props.min < 0);

function preventNonNumericInput(event) {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ];

  if (event.ctrlKey || event.metaKey || allowedKeys.includes(event.key)) return;

  // Test if the key would result in valid input
  const input = event.target;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const currentValue = input.value || '';
  const valueAfterKeyPress =
    currentValue.substring(0, start) + event.key + currentValue.substring(end);

  // Use helper functions to validate
  const formatted = allowNegative.value
    ? formatInteger(valueAfterKeyPress)
    : formatNonNegativeInteger(valueAfterKeyPress);

  // Allow if the formatted result matches what we would type
  if (formatted === valueAfterKeyPress) return;

  event.preventDefault();
}

function handlePaste(event) {
  event.preventDefault();

  const text = event.clipboardData.getData('text');
  const input = event.target;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const currentValue = input.value || '';

  // Build the value that would result from the paste
  const valueAfterPaste = currentValue.substring(0, start) + text + currentValue.substring(end);

  // Format the entire resulting value
  const formatted = allowNegative.value
    ? formatInteger(valueAfterPaste)
    : formatNonNegativeInteger(valueAfterPaste);

  if (formatted && formatted !== '-') {
    localValue.value = Number(formatted);
  }
}

function decrement() {
  if (props.min === null || localValue.value > props.min) {
    handleUpdateModelValue(localValue.value - 1);
  }
}

function increment() {
  if (props.max === null || localValue.value < props.max) {
    handleUpdateModelValue(localValue.value + 1);
  }
}
</script>
